import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NiModalComponent } from '../shared/lib/form/ni-modal/ni-modal.component';
import { NiButtonComponent } from '../shared/lib/form/ni-button/ni-button.component';
import { LocationItem } from '../core/services/location.service';

@Component({
  selector: 'app-location-selection-modal',
  standalone: true,
  imports: [CommonModule, NiModalComponent, NiButtonComponent],
  template: `
    <NiFormModal
      [isOpen]="isOpen"
      [showCancelIcon]="allowCancel"
      [hasBackdrop]="true"
      [preventBackdropClose]="!allowCancel"
      [title]="modalTitle"
      [className]="'max-w-[520px] w-full'"
      (onClose)="onCancel()"
    >
      <div class="flex flex-col gap-[24px] px-[24px] pb-[32px] font-['archivo']">
        <p class="text-[#444955] text-[16px] leading-[24px]">
          {{ modalDescription }}
        </p>

        <div class="flex flex-col gap-[12px] max-h-[320px] overflow-y-auto">
          <button
            *ngFor="let location of locations"
            type="button"
            class="w-full text-left rounded-[12px] border px-[16px] py-[14px] transition-all duration-200 cursor-pointer"
            [class.border-primary-500]="selectedLocationId === location.id"
            [class.bg-primary-50]="selectedLocationId === location.id"
            [class.ring-2]="selectedLocationId === location.id"
            [class.ring-primary-100]="selectedLocationId === location.id"
            [class.border-[#D0D5DD]]="selectedLocationId !== location.id"
            (click)="selectLocation(location)"
          >
            <div class="flex items-start gap-[12px]">
              <span
                class="mt-[2px] w-[20px] h-[20px] rounded-full border-2 shrink-0 flex items-center justify-center"
                [class.border-primary-500]="selectedLocationId === location.id"
                [class.border-[#D0D5DD]]="selectedLocationId !== location.id"
              >
                <span
                  *ngIf="selectedLocationId === location.id"
                  class="w-[10px] h-[10px] rounded-full bg-primary-500"
                ></span>
              </span>
              <div class="flex flex-col gap-[4px]">
                <span class="text-[#111215] font-semibold text-[16px] leading-[22px]">
                  {{ location.locationName }}
                </span>
                <span class="text-[#667085] text-[14px] leading-[20px]">
                  {{ location.addressLine }}
                </span>
              </div>
            </div>
          </button>
        </div>

        <div class="flex justify-center gap-[12px] pt-[8px]">
          <NiFormButton
            *ngIf="allowCancel"
            label="Cancel"
            color="secondary"
            [fontWeight]="700"
            [fontSize]="18"
            paddingSize="px24_py_14"
            (onClick)="onCancel()"
          />
          <NiFormButton
            [label]="continueLabel"
            color="primary"
            [fontWeight]="700"
            [fontSize]="18"
            paddingSize="px24_py_14"
            [disabled]="!selectedLocationId"
            (onClick)="onContinue()"
          />
        </div>
      </div>
    </NiFormModal>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class LocationSelectionModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() locations: LocationItem[] = [];
  /** When true, staff can dismiss without changing location (in-session switch). */
  @Input() allowCancel = false;
  @Input() title = 'Select Location';
  @Input() description = 'Choose the location you are working at today.';
  @Input() continueLabel = 'Continue';
  @Input() initialLocationId = '';
  @Output() locationSelected = new EventEmitter<LocationItem>();
  @Output() cancelled = new EventEmitter<void>();

  selectedLocationId = '';

  get modalTitle(): string {
    return this.title || 'Select Location';
  }

  get modalDescription(): string {
    return this.description || 'Choose the location you are working at today.';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] || changes['initialLocationId'] || changes['locations']) {
      if (this.isOpen) {
        this.selectedLocationId =
          this.initialLocationId ||
          this.locations.find((l) => l.id === this.selectedLocationId)?.id ||
          this.locations[0]?.id ||
          '';
      }
    }
  }

  selectLocation(location: LocationItem): void {
    this.selectedLocationId = location.id;
  }

  onContinue(): void {
    const location = this.locations.find((l) => l.id === this.selectedLocationId);
    if (location) {
      this.locationSelected.emit(location);
    }
  }

  onCancel(): void {
    if (!this.allowCancel) {
      return;
    }
    this.cancelled.emit();
  }
}
